import { v4 as uuidv4 } from 'uuid';
import {
  NODE,
  NODE_TYPE_PREFIX,
  NODECODE_SEPARATOR,
  CODEPREFIX_TO_NODETYPE_MAP,
  SKIP_TYPE,
  ASSIGNEE,
  MULTI_INSTANCE_APPROVAL_TYPE,
  START_NODE,
  MAX_MIDDLE_NODE,
} from '@/views/basis/auditConfig/components/flow/constants';
import type { NODE_PREFIX_TYPE } from '@/views/basis/auditConfig/components/flow/constants';
import { cloneDeep, uniqueId, uniq } from 'lodash';
import { Message } from '@arco-design/web-vue';
import { getFlowDef, getFlowDefWithCondition } from '@/api/flow';
import useListDataStore from '@/store/modules/list-data';
import useFlowStore from '@/store/modules/flow';
import { nanoid } from 'nanoid';
import { useUserStore } from '@/store';
import { storeToRefs } from 'pinia';

const listDataStore = useListDataStore();
const flowStore = useFlowStore();
const userStore = useUserStore();
const { sysConfigMap } = storeToRefs(userStore);

// 是否是开始节点
export const isStartNode = (code: string) => {
  return code?.startsWith(NODE_TYPE_PREFIX.START + NODECODE_SEPARATOR);
};

// 是否是结束节点
const isEndNode = (code: string) => {
  return code?.startsWith(NODE_TYPE_PREFIX.END + NODECODE_SEPARATOR);
};

// 是否是中间节点
const isMiddleNode = (code: string) => {
  return !isStartNode(code) && !isEndNode(code);
};

// 是否是或签节点
const isAuditOrNode = (code: string) => {
  return code?.startsWith(NODE_TYPE_PREFIX.AUDIT_OR + NODECODE_SEPARATOR);
};
// 是否是条件节点
const isConditionNode = (code: string) => {
  return code?.startsWith(NODE_TYPE_PREFIX.CONDITION + NODECODE_SEPARATOR);
};

// 根据节点获取节点前缀
const getNodeCodePrefixByNode = (node?: Recordable | null) => {
  if (!node) return NODE_TYPE_PREFIX.END;
  const { type, multiInstanceApprovalType } = node;
  if (type === NODE.START) return NODE_TYPE_PREFIX.START;
  if (type === NODE.COPY) return NODE_TYPE_PREFIX.CC;
  if (type === NODE.EXCLUSIVE_GATEWANY) return NODE_TYPE_PREFIX.CONDITION;
  if (multiInstanceApprovalType === MULTI_INSTANCE_APPROVAL_TYPE.AUDIT_OR)
    return NODE_TYPE_PREFIX.AUDIT_OR;
  return NODE_TYPE_PREFIX.AUDIT_AND;
};

// 根据nodeCode获取节点类型
export const getNodeTypeByCode = (
  code: string = '',
  maps: Recordable = CODEPREFIX_TO_NODETYPE_MAP
) => {
  const prefix = code.split(NODECODE_SEPARATOR)?.[0];
  return maps?.[prefix as NODE_PREFIX_TYPE];
};

const getNodeCode = (node: Recordable | null, flowCode: string) => {
  return `${getNodeCodePrefixByNode(
    node
  )}${NODECODE_SEPARATOR}${flowCode}${NODECODE_SEPARATOR}${
    node ? uniqueId() : 1 // 结束节点固定一个值
  }`;
};

// 返回值将传给后端 展开打平递归的节点
export const handleExpandNode = (deepNode: Recordable, flowCode: string) => {
  const nodeList: Recordable[] = [];
  const { nodeCode, name, type, childNode, assignees, conditionNodes } =
    deepNode;
  const nowNodeCode = nodeCode || getNodeCode(deepNode, flowCode);
  const nextNodeCode = getNodeCode(childNode, flowCode);
  let skipList = [];
  if (type === NODE.EXCLUSIVE_GATEWANY) {
    const key = nanoid();
    skipList =
      conditionNodes?.map((item: Recordable, index: number) => {
        if (index < conditionNodes.length - 1) {
          return {
            skipName: item.name,
            skipType: SKIP_TYPE,
            nowNodeCode,
            // 如果当前条件没有子节点但网关节点有子节点，则nextNodeCode为网关节点的子节点
            nextNodeCode:
              !item.childNode && childNode
                ? nextNodeCode
                : getNodeCode(item.childNode, flowCode),
            skipCondition: `${key}${NODECODE_SEPARATOR}${index + 1}`,
            conditionGroups: item.conditionGroups,
          };
        }
        return {
          skipName: item.name,
          skipType: SKIP_TYPE,
          nowNodeCode,
          // 如果当前条件没有子节点但网关节点有子节点，则nextNodeCode为网关节点的子节点
          nextNodeCode:
            !item.childNode && childNode
              ? nextNodeCode
              : getNodeCode(item.childNode, flowCode),
          skipCondition: `${key}${NODECODE_SEPARATOR}default`,
        };
      }) ?? [];
  } else {
    skipList = [
      {
        skipType: SKIP_TYPE,
        nowNodeCode,
        nextNodeCode,
      },
    ];
  }
  const node: Recordable = {
    nodeCode: nowNodeCode,
    nodeName: name,
    skipList,
    ext: '{}', // json字符串
  };

  if (type === NODE.START) {
    // 开始
    nodeList.push(node);
    // 结束
    nodeList.push({
      nodeCode: getNodeCode(null, flowCode),
      nodeName: '结束',
      ext: '{}', // json字符串
    });
  } else {
    // 中间节点
    if (type === NODE.EXCLUSIVE_GATEWANY) {
      const skipConditionConfigs: Recordable = [];
      skipList.forEach((item: Recordable, index: number) => {
        if (index < conditionNodes.length - 1) {
          const skip = {
            relateSkipCondition: item.skipCondition,
            conditionGroups: item.conditionGroups,
          };
          delete item.conditionGroups;
          skipConditionConfigs.push(skip);
        }
      });
      node.nodeConditionConfig = {
        conditionNodeCode: nowNodeCode,
        skipConditionConfigs,
      };
    } else {
      const ext = {
        assign: assignees.map((assignee: Recordable) => {
          const { assigneeType, assigneeIds = [] } = assignee;
          const item: Recordable = {
            type: assigneeType,
          };
          if (assigneeType === ASSIGNEE.SELF) {
            return item;
          }
          return {
            assignees: uniq(assigneeIds), // id list
            type: assigneeType, // type：2-发起人 3-用户 4-角色
          };
        }),
      };
      node.ext = JSON.stringify(ext);
    }
    nodeList.push(node);
  }
  if (type === NODE.EXCLUSIVE_GATEWANY) {
    if (conditionNodes?.length) {
      conditionNodes.forEach((item: Recordable, index: number) => {
        if (!item.childNode) return;
        item.childNode.nodeCode = skipList[index].nextNodeCode;
        const nextNodeList = handleExpandNode(item.childNode, flowCode);
        if (childNode) {
          // 如果网关节点有子节点 则将网关的子节点设置为各分支的结束节点
          nextNodeList.forEach((nextNode: Recordable) => {
            nextNode.skipList.forEach((skip: Recordable) => {
              if (isEndNode(skip.nextNodeCode)) {
                skip.nextNodeCode = nextNodeCode;
              }
            });
          });
        }
        nodeList.push(...nextNodeList);
      });
    }
  }
  if (childNode) {
    childNode.nodeCode = nextNodeCode;
    const nextNodeList = handleExpandNode(childNode, flowCode);
    nodeList.push(...nextNodeList);
  }
  return nodeList;
};

// 处理后端返回的节点列表，返回递归的节点
// tips: 使用时要注意nodeList中ext已经反序列化为对象
export const handleDeepNode = (nodeList: Recordable[]) => {
  const startNode = nodeList.find((n) => isStartNode(n.nodeCode));
  if (!startNode) return cloneDeep(START_NODE);
  const nodeCodeMap = nodeList.reduce((acc: Recordable, node: Recordable) => {
    acc[node.nodeCode] = node;
    return acc;
  }, {} as Recordable);
  const getNextNode = (code: string): Recordable | null => {
    if (!nodeCodeMap[code] || isEndNode(code)) return null;
    const {
      nodeName,
      nodeCode,
      skipList,
      nodeConditionConfig,
      ext: { assign = [] } = {},
    } = nodeCodeMap[code];
    const node: Recordable = {
      name: nodeName,
      type: getNodeTypeByCode(nodeCode),
      nodeCode,
    };
    if (isMiddleNode(nodeCode)) {
      // 中间节点
      node.assignees = assign.map((item: Recordable) => {
        const { assigneeIds, assignees, type } = item;
        const temp: Recordable = {
          rid: uuidv4(),
          assigneeType: type,
        };
        // 发起人本人
        if (type === ASSIGNEE.SELF) return temp;
        return {
          ...temp,
          assigneeIds,
          assignees,
        };
      });
      if (node.type === NODE.APPROVE) {
        node.multiInstanceApprovalType = isAuditOrNode(nodeCode)
          ? MULTI_INSTANCE_APPROVAL_TYPE.AUDIT_OR
          : MULTI_INSTANCE_APPROVAL_TYPE.AUDIT_AND;
      }
    }
    if (skipList?.length) {
      if (node.type === NODE.EXCLUSIVE_GATEWANY) {
        node.conditionNodes = skipList.map((cn: Recordable, idx: number) => {
          const skipCfg = nodeConditionConfig?.skipConditionConfigs?.find(
            (it: any) => it.relateSkipCondition === cn.skipCondition
          );
          const cgs =
            skipCfg?.conditionGroups.map((g: Recordable) => {
              g.conditions?.forEach((c: Recordable) => {
                c.id = nanoid();
                const valueSelectType =
                  flowStore.conditionFieldAndSelectTypeMap[c.field];
                if (
                  [
                    'NUMBER',
                    'DATE',
                    'SEX',
                    'CONVERT_GOODS_STATE',
                    'DESIGN_ORDER_MODE',
                    'DESIGN_ORDER_TYPE',
                  ].includes(valueSelectType)
                ) {
                  c.values = c.values?.map((v: string) =>
                    Number.isNaN(Number(v)) ? 0 : Number(v)
                  );
                }
              });
              return { id: nanoid(), ...g };
            }) ?? [];
          return {
            name: cn.skipName || `条件${idx + 1}`,
            priorityLevel: idx + 1,
            type: NODE.CONDITION,
            conditionGroups: cgs,
            childNode: getNextNode(cn.nextNodeCode),
          };
        });
      } else {
        node.childNode = getNextNode(skipList[0].nextNodeCode);
      }
    }
    return node;
  };
  // 先构建完整树
  const tree = getNextNode(startNode.nodeCode)!;

  return liftMergeNodes(tree);
};
/*
 * A即网关节点
 *      |-> B(2) -> D(4)           |-> B(2) |
 * A(1) |                 ==> A(1) |        | -> D(4)
 *      |-> C(3) -> D(4)           |-> C(3) |
 *
 */
// 处理网关节点有汇聚节点的情况
function liftMergeNodes(tree: Recordable): Recordable {
  const root = cloneDeep(tree);
  const walk = (n: Recordable | null): void => {
    if (!n) return;
    if (n.type === NODE.EXCLUSIVE_GATEWANY && n.conditionNodes?.length) {
      // 当前网关节点分支数量
      const branchCnt = n.conditionNodes.length;
      // 分支节点中出现过的节点数量统计
      const cnt = new Map<string, number>();
      // 统计每个 nodeCode 出现次数（全子树）
      const count = (node: Recordable | null): void => {
        if (!node) return;
        const code = node.nodeCode!;
        cnt.set(code, (cnt.get(code) || 0) + 1);
        if (isConditionNode(code)) {
          walk(node);
        }
        count(node.childNode);
      };
      n.conditionNodes.forEach((cn: Recordable) => count(cn.childNode));
      // 找到汇聚节点
      const convergeCode = Array.from(cnt.entries()).find(
        // 如果某个节点出现的数量等于分支数量，则该节点为汇聚节点
        ([, c]) => c === branchCnt
      )?.[0];
      if (!convergeCode) {
        walk(n.childNode);
        return;
      }
      // 缓存第一份实例
      let first: Recordable | null = null;
      const cut = (node: Recordable | null): Recordable | null => {
        if (!node) return null;
        if (node.nodeCode === convergeCode) {
          if (!first) first = cloneDeep(node);
          return null;
        }
        node.childNode = cut(node.childNode);
        return node;
      };
      n.conditionNodes.forEach((cn: Recordable) => {
        // 在分支节点中将汇聚节点去掉
        cn.childNode = cut(cn.childNode);
      });
      // 汇聚节点挂到网关节点
      if (first) n.childNode = first;
      walk(first);
      return;
    }
    walk(n.childNode);
  };

  walk(root);
  return root;
}

export const isNotNullCondition = (condition: Recordable) => {
  const { values, conditionType, field } = condition;
  if (
    conditionType?.includes('_EMPTY') ||
    conditionType?.includes('_NOT_EMPTY')
  ) {
    // 操作符 为空或者不为空
    return field && conditionType;
  }
  if (conditionType?.includes('_BETWEEN')) {
    // 操作符 介于...之间
    return (
      field &&
      conditionType &&
      values?.length === 2 &&
      values.every((val: any) => val || val === 0)
    );
  }
  return (
    field &&
    conditionType &&
    values &&
    values.length &&
    values.some((val: any) => val || val === 0)
  );
};
const checkConditionNode = (nodeList: Recordable[]) => {
  const conditionNodes = nodeList.filter((node: Recordable) =>
    isConditionNode(node.nodeCode)
  );
  if (!conditionNodes.length) return true;
  for (let index = 0; index < conditionNodes.length; index += 1) {
    const conditionNode = conditionNodes[index];
    const { nodeConditionConfig, skipList } = conditionNode;
    for (let i = 0; i < skipList.length - 1; i += 1) {
      const skipNode = skipList[i];
      const { skipCondition, skipName } = skipNode;
      const { skipConditionConfigs } = nodeConditionConfig;
      const conditionGroups = skipConditionConfigs?.find(
        (item: Recordable) => item.relateSkipCondition === skipCondition
      )?.conditionGroups;
      if (!conditionGroups?.length) {
        Message.error(`请为${skipName}节点设置分组条件！`);
        return false;
      }
      // 条件组中的每个条件都不能为空
      if (
        !conditionGroups.every((conditionGroup: Recordable) =>
          conditionGroup?.conditions.every((condition: Recordable) =>
            isNotNullCondition(condition)
          )
        )
      ) {
        Message.error(`${skipName}节点缺少条件配置！`);
        return false;
      }
    }
  }
  return true;
};

// 提交节点流程校验
export const submitNodeCheck = (nodeList: Recordable[]) => {
  if (!isStartNode(nodeList[0].nodeCode)) {
    Message.error('缺少开始节点！');
    return false;
  }
  if (!nodeList.some((node: Recordable) => isEndNode(node.nodeCode))) {
    Message.error('缺少结束节点！');
    return false;
  }
  if (!nodeList.some((node: Recordable) => isMiddleNode(node.nodeCode))) {
    Message.error('流程必须包含中间节点！');
    return false;
  }
  const maxMiddleNodeLength =
    Number(sysConfigMap.value?.maxFlowBetweenNodeCount ?? 0) || MAX_MIDDLE_NODE;
  if (
    nodeList.filter((node: Recordable) => isMiddleNode(node.nodeCode)).length >
    maxMiddleNodeLength
  ) {
    Message.error(`流程最多可以包含${maxMiddleNodeLength}个中间节点`);
    return false;
  }
  if (!checkConditionNode(nodeList)) {
    return false;
  }
  return true;
};

// 获取角色和用户列表
const getRoleAndUserAll = async (nodeList: Recordable[]) => {
  const isGetRole = nodeList.some((node: Recordable) => {
    if (!isMiddleNode(node.nodeCode)) return false;
    const { assign = [] } = node.ext ? JSON.parse(node.ext) : {};
    return assign.some((ass: Recordable) => ass.type === ASSIGNEE.ROLE);
  });
  const isGetUser = nodeList.some((node: Recordable) => {
    if (!isMiddleNode(node.nodeCode)) return false;
    const { assign = [] } = node.ext ? JSON.parse(node.ext) : {};
    return assign.some((ass: Recordable) => ass.type === ASSIGNEE.ASSIGNEE);
  });
  await listDataStore.getDataListByField({
    ROLE: isGetRole,
    USER: isGetUser,
  });
};

// nodeList反序列化ext，并设置assignee对象
const expandNodeList = (
  nodeList: Recordable[],
  nodeConditionConfigs?: Recordable[]
) => {
  return nodeList.map((node: Recordable) => {
    const { ext = '{}', ...temp } = node;
    if (!isMiddleNode(node.nodeCode)) return { ...temp, ext: JSON.parse(ext) };
    const { assign = [] } = JSON.parse(node.ext);
    const nodeConditionConfig = nodeConditionConfigs?.find(
      (item: Recordable) => {
        return temp.nodeCode === item.conditionNodeCode;
      }
    );
    if (nodeConditionConfig && nodeConditionConfig.skipConditionConfigs) {
      nodeConditionConfig.skipConditionConfigs.forEach(
        (skipConditionConfig: Recordable) => {
          skipConditionConfig.conditionGroups.forEach(
            (conditionGroup: Recordable) => {
              conditionGroup.conditions.forEach((condition: Recordable) => {
                const { field, values = [] } = condition;
                if (!field) return;
                const dataOption = listDataStore.getListDataOptionByFieldAndIds(
                  {
                    field: flowStore.conditionFieldAndSelectTypeMap[field],
                    idList: values,
                  }
                );
                condition.values = dataOption.ids;
              });
            }
          );
        }
      );
    }
    return {
      ...temp,
      ext: {
        assign: assign.map((item: Recordable) => {
          const { assignees, type } = item;
          // 发起人本人
          if (type === ASSIGNEE.SELF) return item;
          const dataOption = listDataStore.getListDataOptionByFieldAndIds({
            field: type === ASSIGNEE.ROLE ? 'ROLE' : 'USER',
            idList: assignees,
          });
          return {
            ...item,
            assigneeIds: dataOption.ids,
            assignees: dataOption.datas,
          };
        }),
      },
      nodeConditionConfig,
    };
  });
};

// 获取nodeList，将nodeList中的ext JSON字符串反序列化，将原先中间节点的assignees id数组改为对象数组，原assignees改名为assigneeIds
export const getFlowNodeList = (code: string) => {
  if (!code) return [];
  return getFlowDef(code).then(async (res: any) => {
    if (res.status === 200 && res.data) {
      const { nodeList = [] } = res.data.defInfo ?? {};
      await getRoleAndUserAll(nodeList);
      return expandNodeList(nodeList);
    }
    return [];
  });
};
// 获取分支节点用到的列表数据
export const getBranchNodeListData = (conditionNodes: Recordable = []) => {
  const obj: Recordable = {};
  conditionNodes.forEach((conditionNode: Recordable) => {
    const { conditionGroups = [] } = conditionNode;
    conditionGroups.forEach((conditionGroup: Recordable) => {
      conditionGroup.conditions?.forEach((condition: Recordable) => {
        const { field } = condition;
        const valueSelectType = flowStore.conditionFieldAndSelectTypeMap[field];
        if (valueSelectType) {
          obj[valueSelectType] = true;
        }
      });
    });
  });
  return listDataStore.getDataListByField(obj);
};
// 获取nodeList，将nodeList中的ext JSON字符串反序列化，将原先中间节点的assignees id数组改为对象数组，原assignees改名为assigneeIds
export const getFlowDefWithConditionNodeList = (code: string) => {
  if (!code) return [];
  return getFlowDefWithCondition(code).then(async (res: any) => {
    if (res.status === 200 && res.data) {
      const { nodeList = [] } = res.data.defInfo ?? {};
      const nodeConditionConfigs = res.data.nodeConditionConfigs ?? [];
      const conditionNodes: Recordable[] = [];
      nodeConditionConfigs.forEach((item: Recordable) => {
        conditionNodes.push(...(item.skipConditionConfigs ?? []));
      });
      await getRoleAndUserAll(nodeList);
      await getBranchNodeListData(conditionNodes);
      return expandNodeList(nodeList, nodeConditionConfigs);
    }
    return [];
  });
};
